import React, { useState, useEffect, useCallback } from "react";
import {
  FiLifeBuoy,
  FiClock,
  FiCheckCircle,
  FiAlertTriangle,
  FiSearch,
  FiRefreshCw,
  FiMessageSquare,
  FiX,
  FiSave,
  FiUser,
  FiMail,
} from "react-icons/fi";
import {
  fetchAdminTickets,
  updateAdminTicket,
  fetchTicketConversation,
} from "../services/aiChatService";
import "./AdminSupportTickets.css";

export default function AdminSupportTickets() {
  const [tickets, setTickets] = useState([]);
  const [metrics, setMetrics] = useState({
    total: 0,
    open: 0,
    inProgress: 0,
    resolved: 0,
    highPriority: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Inspector Modal state
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [editStatus, setEditStatus] = useState("open");
  const [editPriority, setEditPriority] = useState("medium");
  const [editNotes, setEditNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [conversationLogs, setConversationLogs] = useState([]);
  const [isLoadingConvo, setIsLoadingConvo] = useState(false);
  const [updateFeedback, setUpdateFeedback] = useState("");

  const loadTickets = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchAdminTickets({
        status: statusFilter,
        priority: priorityFilter,
        search: searchQuery,
      });

      if (res.success) {
        setTickets(res.tickets || []);
        if (res.metrics) {
          setMetrics(res.metrics);
        }
      }
    } catch (err) {
      console.error("Failed to load admin tickets:", err);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, priorityFilter, searchQuery]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadTickets();
  };

  const openInspector = async (ticket) => {
    setSelectedTicket(ticket);
    setEditStatus(ticket.status);
    setEditPriority(ticket.priority);
    setEditNotes(ticket.resolution_notes || "");
    setUpdateFeedback("");
    setConversationLogs([]);

    // If ticket has a linked session, fetch conversation transcript
    if (ticket.session_id) {
      setIsLoadingConvo(true);
      try {
        const res = await fetchTicketConversation(ticket.id);
        if (res.success && Array.isArray(res.messages)) {
          setConversationLogs(res.messages);
        }
      } catch (err) {
        console.warn("Could not fetch conversation logs:", err);
      } finally {
        setIsLoadingConvo(false);
      }
    }
  };

  const handleUpdateTicket = async () => {
    if (!selectedTicket) return;
    setIsUpdating(true);
    setUpdateFeedback("");

    try {
      const res = await updateAdminTicket(selectedTicket.id, {
        status: editStatus,
        priority: editPriority,
        resolution_notes: editNotes,
      });

      if (res.success) {
        setUpdateFeedback("Ticket updated successfully!");
        setSelectedTicket(res.ticket);
        loadTickets();
      }
    } catch (err) {
      setUpdateFeedback("Failed to update ticket: " + (err.error || err.message));
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <div className="ast-container">
      {/* Header */}
      <div className="ast-header">
        <div>
          <h2>Customer Support & Tickets</h2>
          <p>Manage incoming traveler inquiries, AI chat escalations, and resolution notes</p>
        </div>
      </div>

      {/* Metrics Cards */}
      <div className="ast-metrics-grid">
        <div className="ast-metric-card">
          <div className="ast-metric-icon total">
            <FiLifeBuoy />
          </div>
          <div className="ast-metric-data">
            <h4>{metrics.total}</h4>
            <span>Total Tickets</span>
          </div>
        </div>

        <div className="ast-metric-card">
          <div className="ast-metric-icon open">
            <FiClock />
          </div>
          <div className="ast-metric-data">
            <h4>{metrics.open}</h4>
            <span>Open Tickets</span>
          </div>
        </div>

        <div className="ast-metric-card">
          <div className="ast-metric-icon progress">
            <FiRefreshCw />
          </div>
          <div className="ast-metric-data">
            <h4>{metrics.inProgress}</h4>
            <span>In Progress</span>
          </div>
        </div>

        <div className="ast-metric-card">
          <div className="ast-metric-icon resolved">
            <FiCheckCircle />
          </div>
          <div className="ast-metric-data">
            <h4>{metrics.resolved}</h4>
            <span>Resolved</span>
          </div>
        </div>

        <div className="ast-metric-card">
          <div className="ast-metric-icon high">
            <FiAlertTriangle />
          </div>
          <div className="ast-metric-data">
            <h4>{metrics.highPriority}</h4>
            <span>High Priority</span>
          </div>
        </div>
      </div>

      {/* Controls & Filter Bar */}
      <div className="ast-controls">
        <form className="ast-search-box" onSubmit={handleSearchSubmit}>
          <FiSearch />
          <input
            type="text"
            placeholder="Search tickets by subject, description, traveler..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </form>

        <div className="ast-filter-group">
          <select
            className="ast-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>

          <select
            className="ast-select"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="all">All Priorities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>

          <button className="ast-btn-refresh" onClick={loadTickets} title="Reload Data">
            <FiRefreshCw /> Refresh
          </button>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="ast-table-card">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#94a3b8" }}>
            Loading tickets...
          </div>
        ) : tickets.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 0", color: "#64748b" }}>
            No support tickets match the selected filters.
          </div>
        ) : (
          <table className="ast-table">
            <thead>
              <tr>
                <th>Ticket ID</th>
                <th>Traveler / Contact</th>
                <th>Subject</th>
                <th>Priority</th>
                <th>Status</th>
                <th>AI Session</th>
                <th>Created</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => (
                <tr key={t.id}>
                  <td>
                    <span className="ast-id-badge">#TICK-{t.id}</span>
                  </td>
                  <td>
                    <div className="ast-user-cell">
                      <span className="ast-user-name">
                        {t.user?.full_name || t.guest_name || "Traveler"}
                      </span>
                      <span className="ast-user-email">
                        {t.user?.email || t.guest_email || "No email"}
                      </span>
                    </div>
                  </td>
                  <td style={{ maxWidth: "260px", fontWeight: 500 }}>
                    {t.subject}
                  </td>
                  <td>
                    <span className={`ast-priority-tag ${t.priority}`}>
                      {t.priority}
                    </span>
                  </td>
                  <td>
                    <span className={`ast-status-pill ${t.status}`}>
                      {t.status.replace("_", " ")}
                    </span>
                  </td>
                  <td>
                    {t.session_id ? (
                      <span style={{ color: "#34d399", display: "flex", alignItems: "center", gap: "4px", fontSize: "12px" }}>
                        <FiMessageSquare /> Linked Log
                      </span>
                    ) : (
                      <span style={{ color: "#64748b", fontSize: "12px" }}>Manual Form</span>
                    )}
                  </td>
                  <td style={{ fontSize: "12px", color: "#94a3b8" }}>
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <button
                      className="ast-btn-action"
                      onClick={() => openInspector(t)}
                    >
                      Inspect & Reply
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Ticket Inspector Modal */}
      {selectedTicket && (
        <div className="ast-modal-overlay" onClick={() => setSelectedTicket(null)}>
          <div className="ast-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="ast-modal-header">
              <h3>Support Ticket Inspection: #TICK-{selectedTicket.id}</h3>
              <button
                style={{ background: "transparent", border: "none", color: "#cbd5e1", fontSize: "18px", cursor: "pointer" }}
                onClick={() => setSelectedTicket(null)}
              >
                <FiX />
              </button>
            </div>

            <div className="ast-modal-body">
              {/* Ticket Details Grid */}
              <div className="ast-detail-grid">
                <div className="ast-detail-item">
                  <span className="label"><FiUser /> Traveler</span>
                  <span className="val">{selectedTicket.user?.full_name || selectedTicket.guest_name || "Guest"}</span>
                </div>
                <div className="ast-detail-item">
                  <span className="label"><FiMail /> Contact</span>
                  <span className="val">{selectedTicket.user?.email || selectedTicket.guest_email || "N/A"}</span>
                </div>
                <div className="ast-detail-item">
                  <span className="label">Date Created</span>
                  <span className="val">{new Date(selectedTicket.created_at).toLocaleString()}</span>
                </div>
              </div>

              <div>
                <h4 style={{ margin: "0 0 6px 0", fontSize: "15px", color: "#f8fafc" }}>
                  Subject: {selectedTicket.subject}
                </h4>
                <div style={{ background: "rgba(15, 23, 42, 0.6)", padding: "12px 16px", borderRadius: "8px", border: "1px solid rgba(255, 255, 255, 0.08)", fontSize: "13.5px", color: "#cbd5e1" }}>
                  {selectedTicket.description}
                </div>
              </div>

              {/* Linked AI Chat Conversation Section */}
              {selectedTicket.session_id && (
                <div className="ast-convo-section">
                  <h4><FiMessageSquare /> Attached AI Concierge Conversation Transcript</h4>
                  {isLoadingConvo ? (
                    <p style={{ color: "#94a3b8", fontSize: "13px" }}>Loading chat logs...</p>
                  ) : conversationLogs.length === 0 ? (
                    <p style={{ color: "#64748b", fontSize: "13px" }}>No conversation messages found for this session.</p>
                  ) : (
                    <div className="ast-convo-stream">
                      {conversationLogs.map((m, mIdx) => (
                        <div key={mIdx} className={`ast-convo-msg ${m.sender}`}>
                          <strong style={{ fontSize: "11px", display: "block", marginBottom: "3px", color: m.sender === "user" ? "#38bdf8" : "#34d399" }}>
                            {m.sender === "user" ? "Traveler" : "SoloWay AI Concierge"}:
                          </strong>
                          {m.content}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Management Controls */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "12px", color: "#94a3b8", fontWeight: 600, marginBottom: "6px" }}>
                    Update Status
                  </label>
                  <select
                    className="ast-select"
                    style={{ width: "100%" }}
                    value={editStatus}
                    onChange={(e) => setEditStatus(e.target.value)}
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "12px", color: "#94a3b8", fontWeight: 600, marginBottom: "6px" }}>
                    Update Priority
                  </label>
                  <select
                    className="ast-select"
                    style={{ width: "100%" }}
                    value={editPriority}
                    onChange={(e) => setEditPriority(e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              {/* Resolution Notes */}
              <div>
                <label style={{ display: "block", fontSize: "12px", color: "#94a3b8", fontWeight: 600, marginBottom: "6px" }}>
                  Resolution Notes & Admin Response (Visible to Traveler)
                </label>
                <textarea
                  className="ast-search-box"
                  style={{ width: "100%", height: "90px", boxSizing: "border-box" }}
                  placeholder="Enter resolution details, booking adjustments, or responses to the traveler..."
                  value={editNotes}
                  onChange={(e) => setEditNotes(e.target.value)}
                />
              </div>

              {updateFeedback && (
                <div style={{ color: updateFeedback.includes("success") ? "#34d399" : "#fb7185", fontSize: "13px" }}>
                  {updateFeedback}
                </div>
              )}
            </div>

            <div className="ast-modal-footer">
              <button
                className="ast-btn-refresh"
                onClick={() => setSelectedTicket(null)}
              >
                Close
              </button>
              <button
                className="ast-btn-action"
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
                onClick={handleUpdateTicket}
                disabled={isUpdating}
              >
                <FiSave /> {isUpdating ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
